import { STATUS_CONFIRMED } from '../commons/utils';
import { getRoom } from '../models/Room.model';
import { getPax } from '../models/Pax.model';
import { getReservation, extendReservationData, revertReservationData } from '../models/Reservation.model';
import { reservationTTOOType, getReservationTTOO, revertReservationTTOO } from '../reservationsTTOO/models/Reservation.model';

import { isBetweenPeriod } from '../commons/datesUtils';

function getNewReservation(reservationType, reservationId, extraData) {
  switch (reservationType) {
    case reservationTTOOType:
      return getReservationTTOO(reservationId, extraData);
    default:
      return getReservation(reservationId);
  }
}

function handleBackUpAction(reservationType, reservation, backUpData) {
  switch (reservationType) {
    case reservationTTOOType:
      return revertReservationTTOO(reservation, backUpData);
    default:
      return revertReservationData(reservation, backUpData);
  }
}

function getCloseReservationState(reservationsList) {
  const openedReservation = Object.values(reservationsList).find(reservation => (reservation.editionMode));

  if (!openedReservation) return {};

  const { id } = openedReservation;

  return {
    [id]: {
      ...reservationsList[id],
      editionMode: false
    }
  };
}

function getReservationNewRoomState(roomsList) {
  const totalRooms = roomsList.length;
  const newRoomId = roomsList[totalRooms - 1].id + 1;

  return [
    ...roomsList,
    getRoom(newRoomId)
  ];
}

function getReservationModifiedPaxesListState(paxesList, desiredNumber) {
  const totalPaxes = paxesList.length;
  const newPaxId = paxesList[totalPaxes -1].id + 1;

  const finalNumber = desiredNumber - totalPaxes;

  if (finalNumber < 0) return paxesList.slice(0, finalNumber);

  return [
    ...paxesList,
    ...Array.from(Array(finalNumber).keys()).map(index => (getPax(newPaxId + index)))
  ];
}

function getReservationNewServiceState(servicesList, newService) {
  if (!servicesList && !newService) return [];

  if (!servicesList) return [newService];

  if (!newService) return servicesList;

  return [
    ...servicesList,
    newService
  ];
}

function handleUnsavedReservationsUpdate(reservationId, unsavedReservationsList) {
  const position = unsavedReservationsList.indexOf(reservationId);

  if (position >= 0) return  unsavedReservationsList;

  return [...unsavedReservationsList, reservationId];
}

const initialState = {
  reservationsList: {},
  backUpList: {},
  unsavedReservations: []
};

function reducerReservation (state = initialState, action = { type: ''}) {
  switch (action.type) {
    case 'CLEAN_RESERVATIONS_LISTS': {
      return initialState;
    }
    case 'SET_ALL_RESERVATIONS_LIST': {
      const { reservationsList } = action;

      const mappedList = {};
      reservationsList.forEach((reservation, index) => {
        const id = (index + 1);

        mappedList[id] = {
          id,
          ...reservation,
          editionMode:false,
          dataLoaded:false
        }
      });

      return {
        ...state,
        reservationsList: mappedList,
        backUpList: {},
        unsavedReservations: []
      };
    }
    case 'SET_FULL_RESERVATION_DATA': {
      const { reservationsList, backUpList } = state;
      const { id, response } = action;

      const fullDataReservation = extendReservationData(reservationsList[id], response);

      return {
        ...state,
        reservationsList: {
          ...reservationsList,
          ...getCloseReservationState(reservationsList),
          [id]: {
            ...fullDataReservation,
            editionMode: true,
            dataLoaded: true,
            valorated: true,
            modified: false
          }
        },
        backUpList: {
          ...backUpList,
          [id]: fullDataReservation
        }
      };
    }
    case 'ADD_NEW_RESERVATION': {
      const { reservationsList, unsavedReservations } = state;
      const { reservationType, reservationExtraData } = action;

      const reservationsKeysList = Object.keys(reservationsList);
      const totalOfReservations = reservationsKeysList.length
      const lastOne = reservationsKeysList[totalOfReservations-1]

      const newReservationId = (totalOfReservations === 0)
        ? 1
        : reservationsList[lastOne].id + 1;

      return {
        ...state,
        reservationsList: {
          ...reservationsList,
          ...getCloseReservationState(reservationsList),
          [newReservationId]: {
            ...getNewReservation(reservationType, newReservationId, reservationExtraData),
            dataLoaded: true,
            editionMode: true,
            valorated: false,
            modified: true
          }
        },
        unsavedReservations: [
          ...unsavedReservations,
          newReservationId
        ]
      };
    }
    case 'SET_CONFIRMED_RESERVATION': {
      const { reservationsList, backUpList, unsavedReservations } = state;
      const { id, response } = action;

      const { reservationNumber } = response;

      return {
        ...state,
        reservationsList: {
          ...reservationsList,
          [id]: {
            ...reservationsList[id],
            locator: reservationNumber.toString(),
            status: STATUS_CONFIRMED,
            dataLoaded: true,
            modified: false
          }
        },
        backUpList: {
          ...backUpList,
          [id]: reservationsList[id]
        },
        unsavedReservations: unsavedReservations.filter(item => (item !== id))
      };
    }
    case 'UPDATE_RESERVATION_APPRAISE': {
      const { reservationsList } = state;
      const { id, response} = action;

      if (!response) return { ...state };

      const { rawAmount, reservationAmount, currencyCode } = response;

      return {
        ...state,
        reservationsList: {
          ...reservationsList,
          [id]: {
            ...reservationsList[id],
            amount: (reservationAmount) ? reservationAmount : 0,
            rawAmount: (rawAmount) ? rawAmount : 0,
            currencyCode: (currencyCode) ? currencyCode : reservationsList[id].currencyCode,
            valorated: true
          }
        }
      };
    }
    case 'UPDATE_MODIFIED_RESERVATION': {
      const { reservationsList, backUpList, unsavedReservations } = state;
      const { id } = action;

      return {
        ...state,
        reservationsList: {
          ...reservationsList,
          [id]: {
            ...reservationsList[id],
            valorated: true,
            modified: false
          }
        },
        backUpList: {
          ...backUpList,
          [id]: reservationsList[id]
        },
        unsavedReservations: unsavedReservations.filter(item => (item !== id))
      };
    }
    case 'UNDO_RESERVATION_CAHNGES': {
      const { reservationsList, backUpList, unsavedReservations } = state;
      const { id, reservationType } = action;

      return {
        ...state,
        reservationsList: {
          ...reservationsList,
          [id]: {
            ...handleBackUpAction(reservationType, reservationsList[id], backUpList[id]),
            valorated: true,
            modified: false,
            groupId: 0
          }
        },
        unsavedReservations: unsavedReservations.filter(item => (item !== id))
      };
    }
    case 'ERASE_RESERVATION_FROM_LIST': {
      const { reservationsList, backUpList, unsavedReservations } = state;
      const { id } = action;

      const clonedList = Object.assign({}, reservationsList);
      delete clonedList[id];

      const clonedBackUpList = Object.assign({}, backUpList);
      delete clonedBackUpList[id];

      return {
        ...state,
        reservationsList: clonedList,
        backUpList: clonedBackUpList,
        unsavedReservations: unsavedReservations.filter(item => (item !== id))
      };
    }
    case 'TOGGLE_RESERVATION_VIEW_MODE': {
      const { reservationsList } = state;
      const { id } = action;

      return {
        ...state,
        reservationsList: {
          ...reservationsList,
          ...getCloseReservationState(reservationsList),
          [id]: {
            ...reservationsList[id],
            editionMode: true
          }
        }
      };
    }
    case 'UPDATE_RESERVATION_DATES': {
      const { reservationsList, unsavedReservations } = state;
      const { id, fromDate, toDate } = action;

      return {
        ...state,
        reservationsList: {
          ...reservationsList,
          [id]: {
            ...reservationsList[id],
            fromDate,
            toDate,
            valorated: false,
            modified: true
          }
        },
        unsavedReservations: handleUnsavedReservationsUpdate(id, unsavedReservations)
      };
    }
    case 'UPDATE_RESERVATION_BOOKING_DATE': {
      const { reservationsList, unsavedReservations } = state;
      const { id, bookingDate } = action;

      return {
        ...state,
        reservationsList: {
          ...reservationsList,
          [id]: {
            ...reservationsList[id],
            bookingDate,
            valorated: false,
            modified: true
          }
        },
        unsavedReservations: handleUnsavedReservationsUpdate(id, unsavedReservations)
      };
    }
    case 'UPDATE_RESERVATION_CONTRACT': {
      const { reservationsList, backUpList, unsavedReservations } = state;
      const {
        id,
        rateCode,
        contract,
        modified
      } = action;

      const { modified: currentModified } = reservationsList[id];

      const checkStates = (currentModified) ? {} : { valorated: !modified, modified };

      const backUpContractState = (modified) ? {} : { contract };

      return {
        ...state,
        reservationsList: {
          ...reservationsList,
          [id]: {
            ...reservationsList[id],
            rateCode,
            contract,
            ...checkStates
          }
        },
        backUpList: {
          ...backUpList,
          [id]: {
            ...backUpList[id],
            ...backUpContractState
          }
        },
        unsavedReservations: (modified)
          ? handleUnsavedReservationsUpdate(id, unsavedReservations)
          : [...unsavedReservations]
      };
    }
    case 'ADD_RESERVATION_NEW_ROOM': {
      const { reservationsList, unsavedReservations } = state;
      const { id } = action;

      return {
        ...state,
        reservationsList: {
          ...reservationsList,
          [id]: {
            ...reservationsList[id],
            rooms: getReservationNewRoomState(reservationsList[id].rooms),
            valorated: false,
            modified: true
          }
        },
        unsavedReservations: handleUnsavedReservationsUpdate(id, unsavedReservations)
      };
    }
    case 'ERASE_RESEREVATION_ROOM': {
      const { reservationsList, unsavedReservations } = state;
      const { id, roomId } = action;

      return {
        ...state,
        reservationsList: {
          ...reservationsList,
          [id]: {
            ...reservationsList[id],
            rooms: reservationsList[id].rooms.filter(room => (room.id !== roomId)),
            valorated: false,
            modified: true
          }
        },
        unsavedReservations: handleUnsavedReservationsUpdate(id, unsavedReservations)
      };
    }
    case 'UPDATE_RESERVATION_ROOM_CODE': {
      const { reservationsList, unsavedReservations } = state;
      const { id, roomId, roomCode } = action;

      return {
        ...state,
        reservationsList: {
          ...reservationsList,
          [id]: {
            ...reservationsList[id],
            rooms: reservationsList[id].rooms.map((room) => {
              if (room.id !== roomId) return room;

              return {
                ...room,
                roomCode
              }
            }),
            valorated: false,
            modified: true
          }
        },
        unsavedReservations: handleUnsavedReservationsUpdate(id, unsavedReservations)
      };
    }
    case 'UPDATE_RESERVATION_ROOM_MEAL_PLAN': {
      const { reservationsList, unsavedReservations } = state;
      const { id, roomId, mealPlan } = action;

      return {
        ...state,
        reservationsList: {
          ...reservationsList,
          [id]: {
            ...reservationsList[id],
            rooms: reservationsList[id].rooms.map((room) => {
              if (room.id !== roomId) return room;

              return {
                ...room,
                boardCode: mealPlan
              }
            }),
            valorated: false,
            modified: true
          }
        },
        unsavedReservations: handleUnsavedReservationsUpdate(id, unsavedReservations)
      };
    }
    case 'UPDATE_RESERVATION_ROOM_DISTRIBUTION': {
      const { reservationsList, unsavedReservations } = state;
      const { id, roomId, distributionData } = action;

      return {
        ...state,
        reservationsList: {
          ...reservationsList,
          [id]: {
            ...reservationsList[id],
            rooms: reservationsList[id].rooms.map((room) => {
              if (room.id !== roomId) return room;

              const { distribution } = room;

              return {
                ...room,
                distribution: {
                  ...distribution,
                  ...distributionData
                }
              };
            }),
            valorated: false,
            modified: true
          }
        },
        unsavedReservations: handleUnsavedReservationsUpdate(id, unsavedReservations)
      };
    }
    case 'UPDATE_RESERVATION_PAX_DATA': {
      const { reservationsList, unsavedReservations } = state;
      const { id, roomId, paxData } = action;

      return {
        ...state,
        reservationsList: {
          ...reservationsList,
          [id]: {
            ...reservationsList[id],
            rooms: reservationsList[id].rooms.map((room) => {
              if (room.id !== roomId) return room;

              const { paxes } = room;

              return {
                ...room,
                paxes: paxes.map((pax) => {
                  if (pax.id !== paxData.id) return pax;

                  return {
                    ...pax,
                    ...paxData
                  }
                })
              }
            }),
            valorated: false,
            modified: true
          }
        },
        unsavedReservations: handleUnsavedReservationsUpdate(id, unsavedReservations)
      };
    }
    case 'UPDATE_RESERVATION_PAX_LIST': {
      const { reservationsList, unsavedReservations } = state;
      const { id, roomId, desiredNumber } = action;

      return {
        ...state,
        reservationsList: {
          ...reservationsList,
          [id]: {
            ...reservationsList[id],
            rooms: reservationsList[id].rooms.map((room) => {
              if (room.id !== roomId) return room;

              const { paxes } = room;

              return {
                ...room,
                paxes: getReservationModifiedPaxesListState(paxes, desiredNumber)
              }
            }),
            valorated: false,
            modified: true
          }
        },
        unsavedReservations: handleUnsavedReservationsUpdate(id, unsavedReservations)
      };
    }
    case 'UPDATE_RESERVATION_COMMENT': {
      const { reservationsList, unsavedReservations } = state;
      const { id, comment } = action;

      return {
        ...state,
        reservationsList: {
          ...reservationsList,
          [id]: {
            ...reservationsList[id],
            comment,
            valorated: false,
            modified: true
          }
        },
        unsavedReservations: handleUnsavedReservationsUpdate(id, unsavedReservations)
      };
    }
    case 'ADD_RESERVATION_SERVICE': {
      const { reservationsList, unsavedReservations } = state;
      const { id, roomId, service } = action;

      return {
        ...state,
        reservationsList: {
          ...reservationsList,
          [id]: {
            ...reservationsList[id],
            rooms: reservationsList[id].rooms.map((room) => {
              if (room.id !== roomId) return room;

              const { services } = room;

              return {
                ...room,
                services: getReservationNewServiceState(services, service)
              };
            }),
            valorated: false,
            modified: true
          }
        },
        unsavedReservations: handleUnsavedReservationsUpdate(id, unsavedReservations)
      };
    }
    case 'ERASE_RESERVATION_SERVICE': {
      const { reservationsList, unsavedReservations } = state;
      const { id, roomId, serviceId } = action;

      return {
        ...state,
        reservationsList: {
          ...reservationsList,
          [id]: {
            ...reservationsList[id],
            rooms: reservationsList[id].rooms.map((room) => {
              if (room.id !== roomId) return room;

              const { services } = room;

              return {
                ...room,
                services: services.filter(service => (service.id !== serviceId))
              };
            }),
            valorated: false,
            modified: true
          }
        },
        unsavedReservations: handleUnsavedReservationsUpdate(id, unsavedReservations)
      };
    }
    case 'HANDLE_RESERVATION_SERVICES_BY_DATES': {
      const { reservationsList, unsavedReservations } = state;
      const { id, fromDate, toDate } = action;

      return {
        ...state,
        reservationsList: {
          ...reservationsList,
          [id]: {
            ...reservationsList[id],
            rooms: reservationsList[id].rooms.map((room) => {
              const { services } = room;

              if (!services || services.length === 0) return room;

              return {
                ...room,
                services: services.filter((service) => isBetweenPeriod(service.date, fromDate, toDate, 'DD/MM/YYYY'))
              }
            }),
            valorated: false,
            modified: true
          }
        },
        unsavedReservations: handleUnsavedReservationsUpdate(id, unsavedReservations)
      };
    }
    case 'UPDATE_RESERVATION_SERVICE_DATA': {
      const { reservationsList, unsavedReservations } = state;
      const { id, roomId, serviceData } = action;

      return {
        ...state,
        reservationsList: {
          ...reservationsList,
          [id]: {
            ...reservationsList[id],
            rooms: reservationsList[id].rooms.map((room) => {
              if (room.id !== roomId) return room;

              const { services } = room;

              return {
                ...room,
                services: services.map((service) => {
                  if (service.id !== serviceData.id) return service;

                  return {
                    ...service,
                    ...serviceData
                  }
                })
              };
            }),
            valorated: false,
            modified: true
          }
        },
        unsavedReservations: handleUnsavedReservationsUpdate(id, unsavedReservations)
      };
    }
    case 'UPDATE_RESERVATION_SERVICES_LIST': {
      const { reservationsList, unsavedReservations } = state;
      const { id, roomId, servicesList } = action;

      return {
        ...state,
        reservationsList: {
          ...reservationsList,
          [id]: {
            ...reservationsList[id],
            rooms: reservationsList[id].rooms.map((room) => {
              if (room.id !== roomId) return room;

              return {
                ...room,
                services: servicesList
              }
            }),
            valorated: false,
            modified: true
          }
        },
        unsavedReservations: handleUnsavedReservationsUpdate(id, unsavedReservations)
      };
    }
    case 'UPDATE_RESERVACTION_GROUP': {
      const { reservationsList, unsavedReservations } = state;
      const { id, groupCode } = action;

      return {
        ...state,
        reservationsList: {
          ...reservationsList,
          [id]: {
            ...reservationsList[id],
            groupId: groupCode,
            valorated: false,
            modified: true
          }
        },
        unsavedReservations: handleUnsavedReservationsUpdate(id, unsavedReservations)
      };
    }
    default:
      return state;
  }
}

export default reducerReservation;
